import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from './api';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NumberInput from './shared/NumberInput';
import MoneyInput from './shared/MoneyInput';

const TYPES = { bps: 'Basis points', percent: 'Percent of loan', flat: 'Flat amount' };
const NO_OVERRIDE = { commission_type: 'bps', commission_bps: null, commission_percent: null, commission_flat: null };
const fmt$c = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// The value the lead's override holds for its type, or null when it has none.
const overrideValue = (lead) => {
  const t = lead.commission_type || 'bps';
  return t === 'flat' ? lead.commission_flat : t === 'percent' ? lead.commission_percent : lead.commission_bps;
};

// Per-lead commission override (ticket 207). Without one the lead earns the
// owner's default rate from Commissions > Team & Rates.
export default function LeadCommissionCard({ lead, onSaved }) {
  const hasOverride = overrideValue(lead) != null;
  const [type, setType] = useState(lead.commission_type || 'bps');
  const [value, setValue] = useState(overrideValue(lead) ?? null);
  const [saving, setSaving] = useState(false);

  const amt = Number(lead.loan_amount) || 0;
  const preview = value == null ? null
    : type === 'flat' ? Number(value)
    : type === 'percent' ? amt * (Number(value) / 100)
    : amt * (Number(value) / 10000);

  const save = async (patch, done) => {
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.lead_id}`, patch);
      toast({ title: done });
      onSaved?.();
    } catch (e) { toast({ title: 'Failed', description: errorMessage(e, 'Error') }); }
    setSaving(false);
  };

  const submit = () => save({
    commission_type: type,
    commission_bps: type === 'bps' ? Math.round(value) : null,
    commission_percent: type === 'percent' ? value : null,
    commission_flat: type === 'flat' ? value : null,
  }, 'Commission rate saved');

  const clear = () => {
    setType('bps'); setValue(null);
    save(NO_OVERRIDE, 'Back to your default rate');
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="lead-commission-card">
      <div className="font-serif font-semibold mb-1" style={{ color: '#070B5F' }}>Commission</div>
      <div className="text-xs text-slate-500 mb-3" data-testid="lead-commission-state">
        {hasOverride ? 'Custom rate on this lead.' : 'Uses your default rate.'}
      </div>
      <Label className="text-xs">Rate type</Label>
      <Select value={type} onValueChange={(v) => { setType(v); setValue(null); }}>
        <SelectTrigger className="mt-1 mb-3" data-testid="lead-commission-type"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(TYPES).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Label className="text-xs">{type === 'flat' ? 'Amount' : type === 'percent' ? 'Percent' : 'Basis points (100 = 1%)'}</Label>
      <div className="mt-1">
        {type === 'flat'
          ? <MoneyInput value={value} onChange={setValue} cents data-testid="lead-commission-value" />
          : <NumberInput integer={type === 'bps'} min={0} max={type === 'bps' ? 10000 : 100} value={value} onChange={setValue} data-testid="lead-commission-value" />}
      </div>
      {preview != null && (
        <div className="text-xs text-slate-500 mt-2" data-testid="lead-commission-preview">
          = <b>{fmt$c(preview)}</b>{type !== 'flat' && !amt ? ' (add a loan amount to see dollars)' : ''}
        </div>
      )}
      <div className="flex gap-2 mt-4">
        <button onClick={submit} disabled={saving || value == null || value < 0} data-testid="lead-commission-save" className="btn-gold flex-1 justify-center py-2 text-xs">
          {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save rate'}
        </button>
        {hasOverride && (
          <button onClick={clear} disabled={saving} data-testid="lead-commission-clear" className="btn-outline py-2 text-xs">Use default</button>
        )}
      </div>
    </div>
  );
}
